import React from "react";
import { Descendant, Element as SlateElement } from "slate";
import {MentionElementType} from "./plugins/mentions";
import {Link} from "react-router-dom";
import {type} from "node:os";
import {useModalContext} from "../../providers/ModalProvider";
import {Image} from "./elements/Image";
import {CustomElement, CustomText} from "../../types/slate";
import {read} from "node:fs";
import {Icon} from "../common/Icon";

interface PreviewRendererProps {
  content: Descendant[];
  toHighlightText?: string
}

export const renderElement = ({ attributes, children, element, reading=false }: any) => {
  switch (element.type) {
    case "paragraph":
      return <div {...attributes}>{children}</div>;
    case "block-quote":
      return (
        <blockquote
          {...attributes}
          className="border-l-4 border-blue-500 pl-4 italic text-gray-600 mb-2"
        >
        {children}
        </blockquote>
      );
    case "heading-one":
      return <h1 {...attributes} className="text-2xl font-bold mb-2">{children}</h1>;
    case "heading-two":
      return <h2 {...attributes} className="text-xl font-bold mb-2">{children}</h2>;
    case "heading-three":
      return <h3 {...attributes} className="text-lg font-bold mb-2">{children}</h3>;
    case "heading-four":
      return <h4 {...attributes} className="text-md font-semibold mb-2">{children}</h4>;
    case "heading-five":
      return <h5 {...attributes} className="text-sm font-semibold mb-2">{children}</h5>;
    case "heading-six":
      return <h6 {...attributes} className="text-xs font-semibold mb-2">{children}</h6>;
    case "list-item":
      return <li {...attributes} className="ml-4 list-disc">{children}</li>;
    case "bulleted-list":
      return <ul {...attributes} className="list-disc pl-6">{children}</ul>;
    case "mention":
      return reading ? (
        <Link {...attributes} to={"/users/"+element.userId} className="bg-blue-100 text-blue-1 px-1 rounded">
            @{element.userName}
        {children}
          </Link>
      ) : (
        <span {...attributes} className="bg-blue-100 text-blue-600 px-1 rounded">
            @{element.userName}
        {children}
          </span>
      );
    case "code":
      return (
        <pre
          {...attributes}
          className="bg-gray-100 text-gray-700 p-4 rounded font-mono overflow-x-auto"
        >
            <code>{children}</code>
          </pre>
      );
    case "link":
      return (
        <a href={element.url} target="_blank" className="text-blue-1 underline">
             {children}
        </a>
      );
    case "image":
      const imageClass = element.loading ? "opacity-50" : "";
      return (
        <div {...attributes} className={imageClass}>
          <Image url={element.url} />
             {children}
        </div>
      );
    default:
      return <span {...attributes}>{children}</span>;
  }
};

const renderTextWithLinks = (text: string, toHighlightText: string) => {
  const urlRegex = /(https?:\/\/[^\s\}\]\)]+)/g;
  const parts = text.split(urlRegex);

  const highlight = (str: string, toHighlightText: string, highlightClass = "bg-yellow-4") => {
    if (!toHighlightText) return str;

    const regex = new RegExp(`(${toHighlightText})`, "gi"); // Match the text, case-insensitive
    const parts = str.split(regex); // Split the string into parts based on the match

    return parts.map((part, index) =>
      part.toLowerCase() === toHighlightText.toLowerCase() ? (
        <span key={`highlight-${index}`} className={highlightClass}>
        {part}
      </span>
      ) : (
        <span key={`text-${index}`}>{part}</span>
      )
    );
  };
  return parts.map((part, index) => {
    if (urlRegex.test(part)) {
      //check if image
      if (part.match(/\.(jpeg|jpg|gif|png|webp)$/) != null) {
        return (
          <div key={index} className="my-2">
            <Image url={part} />
          </div>
        );
      }
      return (
        <a key={index} href={part} className="text-blue-1 underline break-all" target="_blank" rel="noopener noreferrer">
          {highlight(part, toHighlightText)}
        </a>
      );
    }
    return highlight(part, toHighlightText);
  });
};

export const renderLeaf = ({ attributes, children, leaf, reading=false, toHighlightText }: any) => {
  // Handle text nodes, including empty lines
  if (typeof children === "string" && reading) {
    children = (children || "").split("\n").map((part: any, i: number) => (
      <React.Fragment key={`${i}`}>
        {renderTextWithLinks(part, toHighlightText)} {/* Render text, replacing links with <a> tags */}
      </React.Fragment>
    ));
  }
  if (leaf.bold) {
    children = <span className="font-bold">{children}</span>;
  }
  if (leaf.italic) {
    children = <span className="italic">{children}</span>;
  }
  if (leaf.underline) {
    children = <span className="underline">{children}</span>;
  }
  if (leaf.strikethrough) {
    children = <span className="line-through">{children}</span>;
  }
  if (leaf.code) {
    children = (
      <code className="bg-gray-100 text-gray-700 px-1 rounded font-mono">
        {children}
      </code>
    );
  }

  return <span {...attributes}>{children}</span>;
};

export const PreviewRenderer: React.FC<PreviewRendererProps> = ({ content, toHighlightText }) => {

  const renderNode: any = (node: Descendant, index: number, parent: Descendant, depth:number=0) => {
    if (SlateElement.isElement(node)) {
      node = node as CustomElement;
      const children = node.children as Descendant[];
      const firstChildren = children[0] as CustomText;
      const hasBr = node.children.length === 1 && firstChildren.text === "" && node.type !== "mention";
      return (
        <span className={"element-group"} key={index}>
          {
            renderElement({
              attributes: {  },
              children: (node as CustomElement).children.map((child: Descendant, i) => renderNode(child, i, node, depth+1)),
              element: node,
              reading: true,
            })
          }
          {hasBr && <br />}
        </span>
      )

    }

     return <React.Fragment key={index}>
      {renderLeaf({
        attributes: { },
        children: node.text,
        leaf: node,
        reading: true,
        toHighlightText,
      })}
    </React.Fragment>
  };

  return <>{content.map((node, i) => renderNode(node, i))}</>;
};

export const slateToPlainText = (content: Descendant[]): string => {
  const extractText = (node: Descendant): string => {
    if (SlateElement.isElement(node)) {
      if (node.type === "mention") {
        // Handle mention elements
        const mentionNode = node as MentionElementType; // Use your MentionElement type here
        return ` @${mentionNode.userName || mentionNode.userId || "unknown"} `;
      }

      if (node.type === "image") {
        // Handle mention elements
        return "(image)";
      }


      // Recursively extract text from element children
      return node.children.map(extractText).join("");
    }

    // If it's a text node, return its text
    return node.text || "";
  };

  // Concatenate text from all top-level nodes
  return content.map(extractText).join("\n");
};


export const extractMentions = (nodes: Descendant[]): string[] => {
  const mentions: string[] = [];

  const traverse = (node: Descendant) => {
    // Check if the node is an element and has a type of 'mention'
    if ('type' in node && node.type === 'mention') {
      mentions.push((node as MentionElementType).userId); // Collect the mention's user
    }

    // Recursively traverse children if they exist
    if ('children' in node && Array.isArray(node.children)) {
      node.children.forEach(traverse);
    }
  };

  nodes.forEach(traverse);
  return mentions;
};